import * as UI from "@iyk/ui"
import { FanClip } from "@iyk/vf-core"
import { Res } from "@iyk/vf-server"
import { Array, Effect, Option, Order } from "effect"
import { appConfig } from "../../app-config.ts"
import { Handler } from "../../server/handler.ts"
import { Profile } from "../profile.ts"
import { AlertCard } from "../ui/alert-card.tsx"
import { ExclusiveCardList } from "../ui/exclusive-card-list.tsx"
import { ExclusiveEmptyCard } from "../ui/exclusive-empty-card.tsx"
import { FanClipsUploadCard } from "../ui/fan-clips-upload-card.tsx"
import { FanClipsViewCard } from "../ui/fan-clips-view-card.tsx"
import { Link, Routes } from "../ui/link.tsx"
import { LockedExclusiveCard } from "../ui/locked-exclusive-card.tsx"
import { ShowScheduleLink } from "../ui/show-schedule-item.tsx"
import { UserProfileCard } from "../ui/user-profile-card.tsx"
import type { Route } from "./+types/home.ts"

type LoaderData = {
  nextShow?: Profile.Show
  merchSkus: Array<Profile.Merch>
  exclusives: Array<Profile.Exclusive>
  totalShows: number
  checkInPrompt?: Profile.CheckInPromptInfo
  lockedExclusives: Array<Profile.LockedExclusive>
  displayFanClipsFeedCard: boolean
}

export const loader = Handler.make<LoaderData>("profile-home", ({ api, fanClips, sessions }) => {
  return Effect.all(
    {
      checkInPrompt: api.checkInPrompt(sessions).pipe(Effect.map(Option.getOrUndefined)),
      nextShow: api.findNextShow,
      merchSkus: api.listMerch,
      totalShows: api.listShows.pipe(Effect.map(Array.length)),
      exclusives: sessions.authenticated.pipe(
        Effect.flatMap((session) => api.findUnlockedExclusives(session.user.id)),
        Effect.orElseSucceed(() => [])
      ),
      lockedExclusives: sessions.authenticated.pipe(
        Effect.flatMap((session) => api.findLockedExclusives(Option.some(session.user.id))),
        Effect.orElse(() => api.findLockedExclusives(Option.none()))
      ),
      displayFanClipsFeedCard: fanClips
        .count({ visible: FanClip.Visible.unsafeFrom(true) })
        .pipe(Effect.map((count) => count > 0))
    },
    { concurrency: "unbounded" }
  ).pipe(Effect.map(Res.json))
})

export default function ProfileRoute(props: Route.ComponentProps) {
  const layoutData = props.matches[1].data
  const sortedSkus = Array.sortWith(
    props.loaderData.merchSkus,
    (sku) => (layoutData.user?.ownedMerchSkuIds.includes(sku.id) ? 0 : 1),
    Order.number
  )

  return layoutData.user ? (
    <LoggedInProfile
      user={layoutData.user}
      exclusives={props.loaderData.exclusives}
      nextShow={props.loaderData.nextShow}
      totalShows={props.loaderData.totalShows}
      merchSkus={sortedSkus}
      checkInPrompt={props.loaderData.checkInPrompt}
      lockedExclusives={props.loaderData.lockedExclusives}
      displayFanClipsFeedCard={props.loaderData.displayFanClipsFeedCard}
    />
  ) : (
    <LoggedOutProfile
      nextShow={props.loaderData.nextShow}
      totalShows={props.loaderData.totalShows}
      merchSkus={props.loaderData.merchSkus}
      checkInPrompt={props.loaderData.checkInPrompt}
      lockedExclusives={props.loaderData.lockedExclusives}
      displayFanClipsFeedCard={props.loaderData.displayFanClipsFeedCard}
    />
  )
}

const LoggedInProfile = ({
  user,
  nextShow,
  totalShows,
  alert,
  exclusives,
  checkInPrompt,
  lockedExclusives,
  displayFanClipsFeedCard
}: LoggedInProfile.Props) => (
  <div className="flex flex-col gap-8 px-4">
    <div className="flex flex-col gap-4">
      <UserProfileCard
        userId={user.id}
        username={user.username}
        since={user.memberSince}
        totalProofs={user.totalProofs}
        variant="profile"
      />
      {alert && (
        <AlertCard
          eyebrow={alert.eyebrow}
          title={alert.title}
          subtitle={alert.subtitle}
          announcementTitle={alert.announcementTitle}
          announcementDate={alert.announcementDate}
          description={alert.description}
          kind={alert.kind}
        />
      )}
      {checkInPrompt && (
        <CheckInCard
          title={`At the ${checkInPrompt.show.venue} show?`}
          description="Check in to be entered into a sweepstakes and earn exclusive rewards"
        />
      )}
      <FanClipsUploadCard
        title="Upload your content"
        description="Share your experience for a chance to be featured."
        canUpload={user.attendedShowIds.length > 0}
      />
      {displayFanClipsFeedCard && (
        <FanClipsViewCard
          title="Watch featured clips from fans on the ground"
          images={[
            `${appConfig.s3.assets.vfUrl}/fan_clip_asset_1.jpg`,
            `${appConfig.s3.assets.vfUrl}/fan_clip_asset_2.jpg`,
            `${appConfig.s3.assets.vfUrl}/fan_clip_asset_3.jpg`,
            `${appConfig.s3.assets.vfUrl}/fan_clip_asset_4.jpg`
          ]}
        />
      )}
    </div>
    <section className="flex flex-col gap-4">
      <h2 className="text-base">Your Unlocks</h2>
      <div className="grid gap-2">
        {Array.isEmptyArray(exclusives) && Array.isEmptyArray(lockedExclusives) ? (
          <ExclusiveEmptyCard />
        ) : null}

        {lockedExclusives.map((lockedExclusive) => (
          <LockedExclusiveCard key={lockedExclusive.id} lockedExclusive={lockedExclusive} />
        ))}
        {!Array.isEmptyArray(exclusives) && <ExclusiveCardList owned={true} exclusives={exclusives} />}
      </div>
    </section>
    {nextShow && <NextShow nextShow={nextShow} showCount={totalShows} />}
  </div>
)

namespace LoggedInProfile {
  export interface Props {
    user: Profile.User
    merchSkus: Array<Profile.Merch>
    exclusives: Array<Profile.Exclusive>
    totalShows: number
    nextShow?: Profile.Show
    alert?: AlertCard.Props
    checkInPrompt?: Profile.CheckInPromptInfo
    lockedExclusives: Array<Profile.LockedExclusive>
    displayFanClipsFeedCard: boolean
  }
}

// TODO: add login button and auth sheet
const LoggedOutProfile = ({
  nextShow,
  totalShows,
  checkInPrompt,
  lockedExclusives,
  displayFanClipsFeedCard
}: LoggedOutProfile.Props) => (
  <div className="flex flex-col gap-8 px-4">
    <div className="flex flex-col gap-4">
      <section
        className={UI.cx(
          "flex flex-col items-center justify-center aspect-video",
          "p-8 text-sm text-center text-balance relative bg-cover rounded",
          "border border-white/[10%] bg-white/[3%]"
        )}
      >
        <h1>Become a verified fan</h1>
        <p className="text-gray-11">Claim items to begin</p>
        {appConfig.shopUrl && (
          <a
            href={appConfig.shopUrl}
            target="_blank"
            rel="noreferrer"
            className={UI.classForButton({ kind: "outline", size: "sm", className: "mt-4" })}
          >
            Shop items
          </a>
        )}
      </section>
      {checkInPrompt && (
        <CheckInCard
          title={`At the ${checkInPrompt.show.venue} show?`}
          description="Check in to be entered into a sweepstakes and earn exclusive rewards"
        />
      )}
      {displayFanClipsFeedCard && (
        <FanClipsViewCard
          title="Watch featured clips from fans on the ground"
          images={[
            `${appConfig.s3.assets.vfUrl}/fan_clip_asset_1.jpg`,
            `${appConfig.s3.assets.vfUrl}/fan_clip_asset_2.jpg`,
            `${appConfig.s3.assets.vfUrl}/fan_clip_asset_3.jpg`,
            `${appConfig.s3.assets.vfUrl}/fan_clip_asset_4.jpg`
          ]}
        />
      )}
    </div>
    {!Array.isEmptyArray(lockedExclusives) && (
      <section className="flex flex-col gap-4">
        <h2 className="text-base">Unlocks</h2>
        <div className="grid gap-2">
          {lockedExclusives.map((lockedExclusive) => (
            <LockedExclusiveCard key={lockedExclusive.id} lockedExclusive={lockedExclusive} />
          ))}
        </div>
      </section>
    )}

    {nextShow && (
      <section className="flex flex-col gap-4">
        <div className="flex justify-between items-center">
          <h2 className="text-base">Next show</h2>
          <Link to={Routes.tourSchedule} className={UI.classForLink({ className: "text-sm" })}>
            View all ({totalShows})
          </Link>
        </div>
        {nextShow && (
          <ShowScheduleLink
            id={nextShow.id}
            city={nextShow.city}
            state={nextShow.state}
            dayOfMonth={nextShow.dayOfMonth}
            dayOfWeek={nextShow.dayOfWeek}
            time={nextShow.time}
            venue={nextShow.venue}
            url={nextShow.url}
            month={nextShow.month}
            checked={false}
          />
        )}
      </section>
    )}
  </div>
)

namespace LoggedOutProfile {
  export interface Props {
    nextShow?: Profile.Show
    totalShows: number
    merchSkus: Array<Profile.Merch>
    checkInPrompt?: Profile.CheckInPromptInfo
    lockedExclusives: Array<Profile.LockedExclusive>
    displayFanClipsFeedCard: boolean
  }
}

const NextShow = ({ nextShow, showCount }: NextShow.Props) => (
  <section className="flex flex-col gap-4">
    <div className="flex justify-between items-center">
      <h2 className="text-base">Next show</h2>
      <Link to={Routes.tourSchedule} className={UI.classForLink({ className: "text-sm" })}>
        View all ({showCount})
      </Link>
    </div>
    {nextShow && (
      <ShowScheduleLink
        id={nextShow.id}
        city={nextShow.city}
        state={nextShow.state}
        dayOfMonth={nextShow.dayOfMonth}
        dayOfWeek={nextShow.dayOfWeek}
        time={nextShow.time}
        venue={nextShow.venue}
        url={nextShow.url}
        month={nextShow.month}
        checked={false}
      />
    )}
  </section>
)

namespace NextShow {
  export interface Props {
    nextShow?: Profile.Show
    showCount: number
  }
}

const CheckInCard = ({ title, description }: CheckInCard.Props) => (
  <section className="flex flex-col gap-4 p-4 bshadow bshadow-gray-5">
    <div className="grid gap-1">
      <h3 className="text-sm text-gray-12">{title}</h3>
      <p className="text-sm text-gray-11">{description}</p>
    </div>
    <Link
      to={Routes.checkIn}
      className={UI.classForButton({ size: "sm", kind: "solid", className: "w-max" })}
    >
      Check in
    </Link>
  </section>
)

namespace CheckInCard {
  export interface Props {
    title: string
    description: string
  }
}
